import { useMemo } from 'react';
import { Controller, ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { down, up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import { ContactStage, ContactStatus } from '@lgg/isomorphic/types/__generated__/graphql';
import { useCampaignListForSelect } from 'src/components/domain/campaign/hooks/use-campaign-list-for-select';
import { ContactAddressInput } from 'src/components/domain/contacts/contact-form/contact-address-input';
import {
  ContactFormValues,
  PossibleExtraFields,
} from 'src/components/domain/contacts/contact-form/contact-form.schema';
import { ContactStatusBadge } from 'src/components/domain/contacts/contact-status-element';
import { ContactStatusModal } from 'src/components/domain/contacts/contact-status-modal';
import { useContactSourceListForSelect } from 'src/components/domain/contacts/hooks/use-contact-source-list-for-select';
import { CompanyUsersSelect } from 'src/components/domain/users/components/company-users-select';
import {
  BigTextInput,
  InlineInputWrapper,
  InlineTextInput,
} from 'src/components/general/inputs/inline-text-inputs';
import { InternationalPhoneInput } from 'src/components/general/inputs/international-phone-input';
import { SelectOption } from 'src/components/general/inputs/select/select';
import {
  InlineFormSelect,
  inlineFormSelectStyles,
} from 'src/components/general/modals/inline-form/inline-form-select';
import {
  AddExtraFieldButton,
  ExtraOptionIcon,
  InlineFormDateTimePicker,
  InlineFormUserSelect,
  UserSelectPlaceholder,
  UserSelectValue,
} from 'src/components/general/modals/inline-form/shared';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { ContactTagSelect } from 'src/components/pages/contacts/components/contact-tag-select';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useDateHelpers } from 'src/hooks/use-date-helpers';
import { useVisible } from 'src/hooks/use-visible';

export type ContactStageAndStatus = { stage: ContactStage; status: ContactStatus };

// - 'modal': status, assignee and tags are rendered in the modal's bottom content on
//   desktop (see ContactFormBottomContent) and as regular rows only on mobile.
// - 'stacked': every field is always rendered as a row (e.g. the contact details panel).
export type ContactFormFieldsLayout = 'modal' | 'stacked';

const NameInputsContainer = styled(FlexRow)`
  width: 100%;

  & > :first-child {
    border-right: 1px solid ${({ theme }) => theme.colors.porcelain};
  }

  & > * {
    width: 100%;
    padding: 8px 10px;
  }
`;

const StyledContactStatusBadge = styled(ContactStatusBadge)`
  border: none;
  height: 32px;

  ${down('md')} {
    width: max-content;
  }

  .arrow-icon {
    display: none;
  }
`;

const BottomContentContainer = styled(FlexRow)`
  gap: 8px;
  width: 100%;
  flex-wrap: wrap;
`;

const bottomSelectStyles = css`
  width: max-content;

  .lgg-select__control {
    min-height: unset;
    height: 30px;
    padding: 3px 8px;

    .lgg-select__indicators {
      padding: 0 0 0 5px;
    }
  }
`;

const DesktopUserSelectValue = styled(UserSelectValue)`
  gap: 5px;

  .value-label {
    font-size: 12px;
    line-height: 15px;
  }
`;

const DesktopUserSelectPlaceholder = styled(UserSelectPlaceholder)`
  gap: 5px;

  .placeholder-label {
    font-size: 12px;
    line-height: 15px;
  }
`;

const ViewAllButton = styled.div`
  color: #2d98da;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  line-height: 15px;
  cursor: pointer;
  margin-top: 12.5px;
`;

export const OwnerSelect = styled(CompanyUsersSelect)`
  ${bottomSelectStyles}

  ${down('md')} {
    ${inlineFormSelectStyles};
  }
` as typeof CompanyUsersSelect;

const hiddenMultiValueRemoveStyles = css`
  .lgg-select__control {
    .lgg-select__value-container.lgg-select__value-container--is-multi {
      .lgg-select__multi-value {
        padding: 4px 6px;

        .lgg-select__multi-value__remove {
          display: none;
        }
      }
    }
  }
`;

const TagSelector = styled(ContactTagSelect)`
  ${bottomSelectStyles}

  ${down('md')} {
    ${inlineFormSelectStyles};
  }

  ${hiddenMultiValueRemoveStyles}
` as typeof ContactTagSelect;

const StackedTagSelector = styled(ContactTagSelect)`
  ${inlineFormSelectStyles};
  ${hiddenMultiValueRemoveStyles}
` as typeof ContactTagSelect;

type ContactTagSelectFieldProps = {
  field: ControllerRenderProps<ContactFormValues, 'tags'>;
  layout: ContactFormFieldsLayout;
};

const ContactTagSelectField = ({ field, layout }: ContactTagSelectFieldProps) => {
  const { t } = useTranslation(['common']);
  const breakpointUpMd = useBreakpoint(up('md'));
  const Selector = layout === 'stacked' ? StackedTagSelector : TagSelector;

  return (
    <Selector
      isCreatable
      mobileLabel={t('common:tags')}
      selectedTagsIds={
        field.value
          ?.filter((tag) => !tag.startsWith('new-tag-'))
          ?.map((tag) => parseInt(tag)) ?? []
      }
      isMulti
      onChange={(existingTagIds, newTagNames) => {
        field.onChange([
          ...existingTagIds.map((id) => id.toString()),
          ...newTagNames.map((name) => `new-tag-${name}`),
        ]);
      }}
      hideIndicators={!field.value?.length}
      multiValueMaxToShow={breakpointUpMd && layout === 'modal' ? 1 : 2}
      isClearable={false}
      placeholder={t('common:tags')}
      name="add-contact-field-tags"
    />
  );
};

type ContactNameInputsProps = {
  form: UseFormReturn<ContactFormValues>;
};

export const ContactNameInputs = ({ form }: ContactNameInputsProps) => {
  const { t } = useTranslation(['contacts']);
  const firstNameError = form.formState.errors.firstName?.message;
  const lastNameError = form.formState.errors.lastName?.message;

  return (
    <NameInputsContainer>
      <span>
        <Controller
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <BigTextInput
              {...field}
              data-lgg-id="add-contact-field-first-name"
              showErrorMessage
              error={firstNameError}
              placeholder={t('contacts:createContactModal.fields.firstName.placeholder')}
              label={t('contacts:createContactModal.fields.firstName.label')}
            />
          )}
        />
      </span>
      <span>
        <Controller
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <BigTextInput
              {...field}
              data-lgg-id="add-contact-field-last-name"
              showErrorMessage
              error={lastNameError}
              placeholder={t('contacts:createContactModal.fields.lastName.placeholder')}
              label={t('contacts:createContactModal.fields.lastName.label')}
            />
          )}
        />
      </span>
    </NameInputsContainer>
  );
};

type StageAndStatusProps = {
  stageAndStatus: ContactStageAndStatus | undefined;
  onStageAndStatusChange: (stageAndStatus: ContactStageAndStatus) => void;
  statusModalTitle: string;
};

type ContactFormBottomContentProps = StageAndStatusProps & {
  form: UseFormReturn<ContactFormValues>;
};

export const ContactFormBottomContent = ({
  form,
  stageAndStatus,
  onStageAndStatusChange,
  statusModalTitle,
}: ContactFormBottomContentProps) => {
  const { t } = useTranslation(['common']);
  const statusModalVisibilityHandler = useVisible();

  return (
    <BottomContentContainer>
      <Controller
        control={form.control}
        name="statusId"
        render={({ field }) => (
          <>
            {stageAndStatus?.stage && stageAndStatus?.status ? (
              <ContactStatusBadge
                variant="inline"
                matchStageColor
                stage={stageAndStatus.stage}
                status={stageAndStatus.status}
                onClick={statusModalVisibilityHandler.show}
              />
            ) : null}

            <ContactStatusModal
              onClose={statusModalVisibilityHandler.close}
              onSubmit={(stage, status) => {
                onStageAndStatusChange({ stage, status });
                field.onChange(Number(status.id));
                statusModalVisibilityHandler.close();
              }}
              headerTitle={statusModalTitle}
              statusModalVisibilityHandler={statusModalVisibilityHandler}
              loadingOverride={<></>}
            />
          </>
        )}
      />
      <Controller
        control={form.control}
        name="ownerId"
        render={({ field }) => (
          <OwnerSelect
            mobileLabel={t('common:assignee')}
            selectedUsersIds={field.value ? [field.value] : []}
            isMulti={false}
            onChange={(option) => {
              field.onChange(
                !option || option.value === field.value ? null : option.value,
              );
            }}
            hideIndicators={!field.value}
            renderValue={(value) => <DesktopUserSelectValue value={value} />}
            renderPlaceholder={() => (
              <DesktopUserSelectPlaceholder placeholder={t('common:assignee')} />
            )}
            isClearable
            name="add-contact-field-owner"
          />
        )}
      />
      <Controller
        control={form.control}
        name="tags"
        render={({ field }) => <ContactTagSelectField field={field} layout="modal" />}
      />
    </BottomContentContainer>
  );
};

type ContactFormFieldsProps = StageAndStatusProps & {
  form: UseFormReturn<ContactFormValues>;
  layout: ContactFormFieldsLayout;
  extraFieldsToShow: PossibleExtraFields[];
  onExtraFieldsToShowChange: (fields: PossibleExtraFields[]) => void;
  defaultAddress: ContactFormValues['address'];
  variant?: 'regular' | 'light';
  onViewAll?: () => void;
};

const getExtraFieldOptionTestId = (field: PossibleExtraFields) =>
  `add-contact-extra-field-option-${field}`;

export const ContactFormFields = ({
  form,
  layout,
  stageAndStatus,
  onStageAndStatusChange,
  statusModalTitle,
  extraFieldsToShow,
  onExtraFieldsToShowChange,
  defaultAddress,
  variant = 'regular',
  onViewAll,
}: ContactFormFieldsProps) => {
  const { t } = useTranslation(['common', 'contacts']);
  const { format, parseDate } = useDateHelpers();
  const { campaignOptions, loadingCampaignOptions } = useCampaignListForSelect();
  const { sourceOptions, loadingSourceOptions } = useContactSourceListForSelect();
  const statusModalVisibilityHandler = useVisible();
  const breakpointDownMd = useBreakpoint(down('md'));
  const breakpointUpMd = useBreakpoint(up('md'));
  const showRowsForBottomFields = layout === 'stacked' || breakpointDownMd;
  const RowOwnerSelect = layout === 'stacked' ? InlineFormUserSelect : OwnerSelect;

  const isRegularVariant = useMemo(() => variant === 'regular', [variant]);
  const showViewAllButton =
    !isRegularVariant && breakpointUpMd && onViewAll && extraFieldsToShow.length > 0;

  const extraFields = useMemo(() => {
    return (showViewAllButton ? [extraFieldsToShow[0]] : extraFieldsToShow).map(
      (field) => {
        // eslint-disable-next-line custom-rules/require-try-catch-for-exhaustive
        return match(field)
          .with(PossibleExtraFields.SECONDARY_PHONE, () => null)
          .with(PossibleExtraFields.GENDER, () => (
            <Controller
              key={`${field}-${PossibleExtraFields.GENDER}`}
              control={form.control}
              name="gender"
              render={({ field, fieldState }) => {
                const error = fieldState.error?.message;
                const options = [
                  {
                    label: t('common:male'),
                    value: 'M',
                  },
                  { label: t('common:female'), value: 'F' },
                ];

                return (
                  <InlineInputWrapper
                    label={t('common:gender')}
                    error={error}
                    icon={isRegularVariant ? 'gender' : ''}
                  >
                    <InlineFormSelect
                      {...field}
                      name="add-contact-field-gender"
                      mobileLabel={t('common:gender')}
                      value={options.find((option) => option.value === field.value)}
                      hasError={Boolean(error)}
                      placeholder={t(
                        'contacts:createContactModal.fields.gender.placeholder',
                      )}
                      isClearable
                      isSearchable={false}
                      options={options}
                      hideIndicators
                      onChange={(value) => {
                        field.onChange((value as SelectOption<string>)?.value);
                      }}
                    />
                  </InlineInputWrapper>
                );
              }}
            />
          ))
          .with(PossibleExtraFields.BIRTHDATE, () => (
            <Controller
              key={`${field}-${PossibleExtraFields.BIRTHDATE}`}
              control={form.control}
              name="birthdate"
              render={({ field, fieldState }) => {
                return (
                  <InlineInputWrapper
                    label={t('contacts:createContactModal.fields.birthday.label')}
                    error={fieldState.error?.message}
                    icon={isRegularVariant ? 'birthday' : ''}
                  >
                    <InlineFormDateTimePicker
                      showSuffixIcon={false}
                      testId="add-contact-field-birthdate"
                      allowClear={false}
                      onApply={(date) => {
                        field.onChange(date ? format(date, 'YYYY-MM-DD') : null);
                      }}
                      inputValue={
                        field.value ? parseDate(field.value, 'YYYY-MM-DD') : undefined
                      }
                      inputDateFormat="MMM D, YYYY"
                    />
                  </InlineInputWrapper>
                );
              }}
            />
          ))
          .with(PossibleExtraFields.CAMPAIGN, () => (
            <Controller
              key={`${field}-${PossibleExtraFields.CAMPAIGN}`}
              control={form.control}
              name="campaignId"
              render={({ field, fieldState }) => {
                return (
                  <InlineInputWrapper
                    label={t('common:campaign')}
                    error={fieldState.error?.message}
                    icon={isRegularVariant ? 'activityCampaign' : ''}
                  >
                    <InlineFormSelect
                      key={field.value ? 'has-value' : 'no-value'}
                      name="add-contact-field-campaign"
                      value={campaignOptions.find(
                        (option) => option.value === field.value,
                      )}
                      options={campaignOptions}
                      isLoading={loadingCampaignOptions}
                      placeholder={t('common:inputPlaceholder.campaign')}
                      mobileLabel={t('common:campaign')}
                      isSearchable
                      isClearable={false}
                      label=""
                      hideIndicators
                      onChange={(option) =>
                        field.onChange(
                          option?.value === field.value ? null : option?.value,
                        )
                      }
                    />
                  </InlineInputWrapper>
                );
              }}
            />
          ))
          .with(PossibleExtraFields.SOURCE, () => (
            <Controller
              key={`${field}-${PossibleExtraFields.SOURCE}`}
              control={form.control}
              name="sourceId"
              render={({ field, fieldState }) => {
                return (
                  <InlineInputWrapper
                    label={t('common:source')}
                    error={fieldState.error?.message}
                    icon={isRegularVariant ? 'activitySource' : ''}
                  >
                    <InlineFormSelect
                      key={field.value ? 'has-value' : 'no-value'}
                      name="add-contact-field-source"
                      value={sourceOptions.find((option) => option.value === field.value)}
                      mobileLabel={t('common:source')}
                      options={sourceOptions}
                      isLoading={loadingSourceOptions}
                      placeholder={t('common:inputPlaceholder.source')}
                      isSearchable
                      isClearable={false}
                      label=""
                      hideIndicators
                      onChange={(option) =>
                        field.onChange(
                          option?.value === field.value ? null : option?.value,
                        )
                      }
                    />
                  </InlineInputWrapper>
                );
              }}
            />
          ))
          .with(PossibleExtraFields.ADDRESS, () => (
            <Controller
              key={`${field}-${PossibleExtraFields.ADDRESS}`}
              control={form.control}
              name="address"
              render={({ field, fieldState }) => {
                return (
                  <InlineInputWrapper
                    label={t('common:address')}
                    icon={isRegularVariant ? 'location' : ''}
                    error={fieldState.error?.message}
                  >
                    <ContactAddressInput field={field} defaultValues={defaultAddress} />
                  </InlineInputWrapper>
                );
              }}
            />
          ))
          .exhaustive();
      },
    );
  }, [
    showViewAllButton,
    extraFieldsToShow,
    form.control,
    t,
    isRegularVariant,
    parseDate,
    format,
    campaignOptions,
    loadingCampaignOptions,
    sourceOptions,
    loadingSourceOptions,
    defaultAddress,
  ]);

  const addFieldOptions = useMemo(() => {
    const allOptions = [
      {
        label: t('contacts:createContactModal.fields.secondaryPhone.label_short'),
        value: PossibleExtraFields.SECONDARY_PHONE,
        icon: 'phone',
      },
      {
        label: t('common:gender'),
        value: PossibleExtraFields.GENDER,
        icon: 'gender',
      },
      {
        label: t('contacts:createContactModal.fields.birthday.label'),
        value: PossibleExtraFields.BIRTHDATE,
        icon: 'birthday',
      },
      {
        label: t('common:campaign'),
        value: PossibleExtraFields.CAMPAIGN,
        icon: 'activityCampaign',
      },
      {
        label: t('common:source'),
        value: PossibleExtraFields.SOURCE,
        icon: 'activitySource',
      },
      {
        label: t('common:address'),
        value: PossibleExtraFields.ADDRESS,
        icon: 'location',
      },
    ];

    return allOptions
      .filter(({ value }) => !extraFieldsToShow.includes(value))
      .map(({ label, value, icon }) => ({
        label,
        value,
        'data-lgg-id': getExtraFieldOptionTestId(value),
        icon: <ExtraOptionIcon type={icon} />,
        onClick: () => {
          onExtraFieldsToShowChange([...extraFieldsToShow, value]);
        },
      }));
  }, [extraFieldsToShow, onExtraFieldsToShowChange, t]);

  const bottomContent = useMemo(() => {
    const addFieldOption = addFieldOptions.length > 0 && (
      <AddExtraFieldButton options={addFieldOptions} />
    );

    if (isRegularVariant) {
      return addFieldOption;
    }

    const filledExtraFields = extraFieldsToShow.length;

    return showViewAllButton ? (
      <ViewAllButton data-lgg-id="contact-details-form-view-all" onClick={onViewAll}>
        {t('common:actions.viewAll', { count: 8 + filledExtraFields })}
      </ViewAllButton>
    ) : (
      addFieldOption
    );
  }, [
    addFieldOptions,
    showViewAllButton,
    extraFieldsToShow.length,
    isRegularVariant,
    onViewAll,
    t,
  ]);

  return (
    <>
      <FlexColumn>
        {!isRegularVariant ? (
          <>
            <Controller
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <InlineTextInput
                  {...field}
                  data-lgg-id="add-contact-field-first-name"
                  label={t('contacts:createContactModal.fields.firstName.label')}
                  error={form.formState.errors.firstName?.message}
                  placeholder={t(
                    'contacts:createContactModal.fields.firstName.placeholder',
                  )}
                />
              )}
            />
            <Controller
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <InlineTextInput
                  {...field}
                  data-lgg-id="add-contact-field-last-name"
                  label={t('contacts:createContactModal.fields.lastName.label')}
                  error={form.formState.errors.lastName?.message}
                  placeholder={t(
                    'contacts:createContactModal.fields.lastName.placeholder',
                  )}
                />
              )}
            />
          </>
        ) : null}
        {showRowsForBottomFields ? (
          <Controller
            control={form.control}
            name="statusId"
            render={({ field }) => (
              <InlineInputWrapper
                label={t('common:status')}
                icon={isRegularVariant ? 'status' : ''}
                error={form.formState.errors.statusId?.message}
              >
                {stageAndStatus?.stage && stageAndStatus?.status ? (
                  <>
                    <StyledContactStatusBadge
                      variant="regular"
                      matchStageColor
                      stage={stageAndStatus.stage}
                      status={stageAndStatus.status}
                      onClick={statusModalVisibilityHandler.show}
                    />
                    <ContactStatusModal
                      onClose={statusModalVisibilityHandler.close}
                      onSubmit={(stage, status) => {
                        onStageAndStatusChange({ stage, status });
                        field.onChange(Number(status.id));
                        statusModalVisibilityHandler.close();
                      }}
                      headerTitle={statusModalTitle}
                      statusModalVisibilityHandler={statusModalVisibilityHandler}
                      loadingOverride={<></>}
                    />
                  </>
                ) : null}
              </InlineInputWrapper>
            )}
          />
        ) : null}
        {showRowsForBottomFields ? (
          <Controller
            control={form.control}
            name="ownerId"
            render={({ field }) => (
              <InlineInputWrapper
                label={t('common:assignee')}
                icon={isRegularVariant ? 'assignedto' : ''}
                error={form.formState.errors.ownerId?.message}
              >
                <RowOwnerSelect
                  mobileLabel={t('common:assignee')}
                  selectedUsersIds={field.value ? [field.value] : []}
                  isMulti={false}
                  onChange={(value) => {
                    field.onChange(value?.value ?? null);
                  }}
                  hideIndicators={!isRegularVariant}
                  renderValue={(value) => <UserSelectValue value={value} />}
                  renderPlaceholder={() => (
                    <UserSelectPlaceholder placeholder={t('common:assignee')} />
                  )}
                  name="ownerId"
                />
              </InlineInputWrapper>
            )}
          />
        ) : null}
        <Controller
          control={form.control}
          name="primaryPhone"
          render={({ field }) => {
            const error = form.formState.errors.primaryPhone?.message;

            return (
              <InlineInputWrapper
                label={t('common:phone')}
                icon={isRegularVariant ? 'phone' : ''}
                error={error}
              >
                <InternationalPhoneInput<ContactFormValues>
                  field={field}
                  testId="add-contact-field-primary-phone"
                  error={error}
                />
              </InlineInputWrapper>
            );
          }}
        />
        {extraFieldsToShow.includes(PossibleExtraFields.SECONDARY_PHONE) ? (
          <Controller
            control={form.control}
            name="secondaryPhone"
            render={({ field, fieldState }) => {
              const error = fieldState.error?.message;

              return (
                <InlineInputWrapper
                  label={t(
                    'contacts:createContactModal.fields.secondaryPhone.label_short',
                  )}
                  icon={isRegularVariant ? 'phone' : ''}
                  error={error}
                >
                  <InternationalPhoneInput<ContactFormValues>
                    field={field}
                    testId="add-contact-field-secondary-phone"
                    error={error}
                  />
                </InlineInputWrapper>
              );
            }}
          />
        ) : null}
        <Controller
          control={form.control}
          name="primaryEmail"
          render={({ field }) => (
            <InlineTextInput
              {...field}
              onChange={(e) => {
                field.onChange(e.target.value.trim());
              }}
              data-lgg-id="add-contact-field-primary-email"
              label={t('common:email')}
              icon={isRegularVariant ? 'email' : ''}
              error={form.formState.errors.primaryEmail?.message}
              placeholder={t('contacts:createContactModal.fields.email.placeholder')}
            />
          )}
        />
        <Controller
          control={form.control}
          name="interest"
          render={({ field }) => (
            <InlineTextInput
              {...field}
              data-lgg-id="add-contact-field-interest"
              label={t('common:interest')}
              icon={isRegularVariant ? 'interest' : ''}
              error={form.formState.errors.interest?.message}
              placeholder={t('contacts:createContactModal.fields.interest.placeholder')}
            />
          )}
        />
        {showRowsForBottomFields ? (
          <Controller
            control={form.control}
            name="tags"
            render={({ field }) => {
              return (
                <InlineInputWrapper
                  label={t('common:tags')}
                  icon={isRegularVariant ? 'tags' : ''}
                  error={form.formState.errors.tags?.message}
                >
                  <ContactTagSelectField field={field} layout={layout} />
                </InlineInputWrapper>
              );
            }}
          />
        ) : null}
        {extraFields}
      </FlexColumn>
      {bottomContent}
    </>
  );
};
